import { Archive, Copy, Edit, MoreHorizontal, StopCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProgramStatus } from "@/graphql";
import { MainProgram, MiniProgram } from "@/types/programs";

type Program = MainProgram | MiniProgram;

interface ProgramMenuProps {
  program: Program;
  status: ProgramStatus;
  isOpen: boolean;
  onClick: () => void;
  onUpdateProgram: (program: Program) => void;
  onCloneProgram: (program: Program) => void;
  onArchiveProgram: (program: Program) => void;
  onEndProgram: (program: Program) => void;
  onClose: () => void;
}

export function ProgramMenu({
  program,
  status,
  isOpen,
  onClick,
  onUpdateProgram,
  onCloneProgram,
  onArchiveProgram,
  onEndProgram,
  onClose,
}: ProgramMenuProps) {
  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          onClick();
        } else {
          onClose();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          intent="secondary"
          size="md"
          className="h-10 w-10"
          onClick={(e) => e.stopPropagation()}
          data-test-id="program-actions-button"
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onUpdateProgram(program);
          }}
          className="cursor-pointer"
        >
          <Edit className="h-4 w-4" />
          Edit Program
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onCloneProgram(program);
          }}
          className="cursor-pointer"
        >
          <Copy className="h-4 w-4" />
          Clone Program
        </DropdownMenuItem>
        {[ProgramStatus.InDesign, ProgramStatus.LaunchReady].includes(
          status,
        ) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onArchiveProgram(program);
              }}
              variant="destructive"
              className="cursor-pointer"
            >
              <Archive className="h-4 w-4" />
              Archive Program
            </DropdownMenuItem>
          </>
        )}
        {[ProgramStatus.Live].includes(status) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEndProgram(program);
              }}
              className="cursor-pointer"
            >
              <StopCircle className="h-4 w-4" />
              End Program
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
